export const buildTemplateSlug = (name = '') => String(name || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .replace(/-{2,}/g, '-');

const TEMPLATE_ACQUISITION_KEYS = new Set(['acq_surface', 'acq_template_id', 'acq_landing_path']);

export const buildTemplateIntentPath = (templateId, acquisition = null) => {
  const params = new URLSearchParams();
  if (templateId) {
    params.set('templateId', templateId);
    params.set('acq_template_id', templateId);
  }
  if (acquisition && typeof acquisition === 'object') {
    for (const [key, rawValue] of Object.entries(acquisition)) {
      if (!TEMPLATE_ACQUISITION_KEYS.has(key)) continue;
      const value = String(rawValue ?? '').trim();
      if (value) params.set(key, value.slice(0, 300));
    }
  }
  const query = params.toString();
  return query ? `/novo-laudo?${query}` : '/novo-laudo';
};

export const sanitizeRedirectPath = (value) => {
  const redirect = String(value || '').trim();
  if (!redirect.startsWith('/') || redirect.startsWith('//')) return '/painel';
  if (redirect.includes('://')) return '/painel';
  return redirect;
};

export const isSeoPublicTemplate = (template) => Boolean(
  template?.is_public &&
  template?.seo_status === 'approved' &&
  template?.seo_sanitized === true
);

const normalizedTemplateKey = (template = {}) => buildTemplateSlug(
  `${template?.seo_slug || ''} ${template?.name || ''}`
);

const includesAny = (value, needles) => needles.some((needle) => value.includes(needle));

const conditionSuffix = (key, conditions) => {
  const match = conditions.find(([needle]) => key.includes(needle));
  return match ? `/${match[1]}` : '';
};

export const getTemplateSeoTaxonomy = (template = {}) => {
  const key = normalizedTemplateKey(template);

  if (template?.category === 'veterinario' || includesAny(key, ['veterinaria', 'veterinario', 'canino', 'felino', 'cao', 'gato'])) {
    const hubPath = '/modelos-laudo/veterinaria';
    const slug = template?.seo_slug || buildTemplateSlug(template?.name);
    return { modality: 'veterinaria', modalityLabel: 'Veterinária', hubPath, path: `${hubPath}/${slug}` };
  }

  if (includesAny(key, ['ressonancia-magnetica', 'ressonancia'])) {
    const hubPath = '/modelos-laudo/ressonancia';
    if (key.includes('coluna-lombar') || (key.includes('lombar') && key.includes('coluna'))) {
      const suffix = conditionSuffix(key, [
        ['hernia', 'hernia-discal'],
        ['protrus', 'protrusao-discal'],
        ['estenose', 'estenose'],
        ['espondilolist', 'espondilolistese'],
      ]);
      return { modality: 'ressonancia', modalityLabel: 'Ressonância magnética', hubPath, path: `${hubPath}/coluna-lombar${suffix}` };
    }
    if (key.includes('joelho')) return { modality: 'ressonancia', modalityLabel: 'Ressonância magnética', hubPath, path: `${hubPath}/joelho` };
    if (key.includes('prostata')) return { modality: 'ressonancia', modalityLabel: 'Ressonância magnética', hubPath, path: `${hubPath}/prostata` };
    return { modality: 'ressonancia', modalityLabel: 'Ressonância magnética', hubPath, path: `${hubPath}/${template?.seo_slug || buildTemplateSlug(template?.name)}` };
  }

  if (includesAny(key, ['tomografia-computadorizada', 'tomografia'])) {
    const hubPath = '/modelos-laudo/tomografia';
    if (key.includes('cranio')) {
      const suffix = conditionSuffix(key, [['hemorragia-intracraniana', 'hemorragia-intracraniana']]);
      return { modality: 'tomografia', modalityLabel: 'Tomografia', hubPath, path: `${hubPath}/cranio${suffix}` };
    }
    if (includesAny(key, ['abdome', 'abdominal'])) return { modality: 'tomografia', modalityLabel: 'Tomografia', hubPath, path: `${hubPath}/abdome` };
    return { modality: 'tomografia', modalityLabel: 'Tomografia', hubPath, path: `${hubPath}/${template?.seo_slug || buildTemplateSlug(template?.name)}` };
  }

  if (includesAny(key, ['eletrocardiograma', 'ecg'])) {
    const hubPath = '/modelos-laudo/ecg';
    if (includesAny(key, ['ritmo-sinusal', 'ecg-normal', 'normal'])) return { modality: 'ecg', modalityLabel: 'ECG', hubPath, path: `${hubPath}/normal` };
    if (key.includes('fibrilacao-atrial')) return { modality: 'ecg', modalityLabel: 'ECG', hubPath, path: `${hubPath}/fibrilacao-atrial` };
    if (includesAny(key, ['bloqueio-atrioventricular', 'bloqueio-av'])) return { modality: 'ecg', modalityLabel: 'ECG', hubPath, path: `${hubPath}/bloqueio-av-primeiro-grau` };
    if (includesAny(key, ['12-derivacoes-adulto', '12-derivacoes'])) return { modality: 'ecg', modalityLabel: 'ECG', hubPath, path: `${hubPath}/12-derivacoes` };
    const fallbackSlug = template?.seo_slug || buildTemplateSlug(template?.name);
    return { modality: 'ecg', modalityLabel: 'ECG', hubPath, path: `${hubPath}/${fallbackSlug}` };
  }

  if (includesAny(key, ['radiografia', 'raio-x'])) {
    const hubPath = '/modelos-laudo/raio-x';
    if (key.includes('torax')) {
      let suffix = conditionSuffix(key, [
        ['pneumonia', 'pneumonia'],
        ['congestao-pulmonar', 'congestao-pulmonar'],
        ['derrame-pleural', 'derrame-pleural'],
        ['pneumotorax', 'pneumotorax'],
        ['atelectasia', 'atelectasia'],
      ]);
      if (!suffix && key.includes('-ap')) suffix = '/ap';
      return { modality: 'raio-x', modalityLabel: 'Raio-X', hubPath, path: `${hubPath}/torax${suffix}` };
    }
    if (key.includes('abdome')) return { modality: 'raio-x', modalityLabel: 'Raio-X', hubPath, path: `${hubPath}/abdome` };
    if (includesAny(key, ['coluna-lombossacra', 'lombossacra'])) return { modality: 'raio-x', modalityLabel: 'Raio-X', hubPath, path: `${hubPath}/coluna-lombossacra` };
    for (const region of ['joelho', 'ombro', 'mao', 'pe']) {
      if (key.includes(region)) return { modality: 'raio-x', modalityLabel: 'Raio-X', hubPath, path: `${hubPath}/${region}` };
    }
    return { modality: 'raio-x', modalityLabel: 'Raio-X', hubPath, path: `${hubPath}/${template?.seo_slug || buildTemplateSlug(template?.name)}` };
  }

  if (includesAny(key, ['ultrassonografia', 'ultrassom'])) {
    const hubPath = '/modelos-laudo/ultrassonografia';
    if (key.includes('abdome')) {
      const suffix = conditionSuffix(key, [
        ['colelitiase', 'colelitiase'],
        ['esteatose-hepatica', 'esteatose-hepatica'],
      ]);
      return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/abdome${suffix}` };
    }
    if (key.includes('tireoide')) {
      const suffix = key.includes('nodulo') ? '/nodulo' : '';
      return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/tireoide${suffix}` };
    }
    if (includesAny(key, ['rins', 'vias-urinarias'])) {
      const suffix = conditionSuffix(key, [
        ['hidronefrose', 'hidronefrose'],
        ['nefrolitiase', 'nefrolitiase'],
      ]);
      return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/rins-vias-urinarias${suffix}` };
    }
    if (key.includes('prostata')) return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/prostata/hiperplasia-prostatica` };
    if (key.includes('partes-moles')) return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/partes-moles` };
    if (key.includes('obstetrica')) return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/obstetrica/primeiro-trimestre` };
    return { modality: 'ultrassonografia', modalityLabel: 'Ultrassonografia', hubPath, path: `${hubPath}/${template?.seo_slug || buildTemplateSlug(template?.name)}` };
  }

  const fallbackSlug = template?.seo_slug || buildTemplateSlug(template?.name);
  return { modality: 'outros', modalityLabel: 'Outros laudos', hubPath: '/modelos-laudo', path: `/modelos-laudo/${fallbackSlug}` };
};

export const buildTemplateSeoPath = (template) => getTemplateSeoTaxonomy(template).path;

export const buildLegacyTemplateSeoPath = (template) =>
  `/modelos-laudo/${template?.seo_slug || buildTemplateSlug(template?.name)}`;

export const templateMatchesPublicPath = (template, pathname = '') => {
  const cleanPath = String(pathname || '').replace(/\/+$/, '') || '/';
  const canonical = buildTemplateSeoPath(template).replace(/\/+$/, '');
  const legacy = buildLegacyTemplateSeoPath(template).replace(/\/+$/, '');
  return cleanPath === canonical || cleanPath === legacy;
};
